import { useCallback, useEffect, useRef, useState } from 'react';
import { Paper, Typography } from '@mui/material';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import { getDetectiveAvailability, getDetectiveConfig } from '../../../services/detectiveApi';
import DetectiveSetup from './DetectiveSetup';
import DetectiveDashboard from './DetectiveDashboard';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalErrorState, PortalSectionHeader, PortalEmptyState,
} from '../../../components/portal/PortalStates';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import { CK, FONT_BODY, sectionCardSx } from '../portalStyles';

// Must match `color` in engines/detective.js — the sidebar, the header avatar and
// the wizard's buttons all claim to be "the Detective indigo".
const DETECTIVE_COLOR = '#3949AB';

// Consecutive background-poll failures tolerated before the error card replaces
// the progress view. Matches InspectorEntry, so at the shared 3s cadence a hard
// fault surfaces after roughly nine seconds.
const MAX_POLL_FAILURES = 3;

// Every Detective state (loading, error, unavailable, transitioning) keeps the
// same page header mounted so the layout never jumps as the config lands.
const detectiveHeader = (
  <PortalPageHeader
    icon={<TravelExploreOutlinedIcon sx={{ fontSize: 24 }} />}
    title="Amazon Detective"
    subtitle="Behavior-graph investigation across your AWS organization"
    color={DETECTIVE_COLOR}
  />
);

// transitionCopy picks the in-progress wording from the config's progressLabel,
// exactly as InspectorEntry does: there is no "disabling" status, the disable and
// update paths both flip the row to in_progress and stamp their label in the same
// UPDATE, and errorMessage is not a usable discriminator because the disable path
// never writes it. Enable is the fallback rather than a matched case because it is
// the one path that can briefly show an empty label before the orchestrator
// goroutine stamps one.
function transitionCopy(progressLabel) {
  const hint = (progressLabel || '').toLowerCase();
  if (hint.includes('disabl')) {
    return {
      title: 'Disabling Amazon Detective…',
      body: 'Removing members from the behavior graph, deleting the graph in every enrolled region and detaching the scoped role permissions Prism attached. This can take a few minutes.',
    };
  }
  if (hint.includes('updat')) {
    return {
      title: 'Updating Amazon Detective…',
      body: 'Applying the requested regions across the behavior graph. Each region gets its own graph, so regions become available as each one finishes.',
    };
  }
  return {
    title: 'Enabling Amazon Detective across accounts and regions…',
    body: 'Creating the behavior graph, inviting each member account and waiting for AWS to verify every root user email address. Enrollment is by invitation, so this can take several minutes.',
  };
}

// DetectiveEntry decides what to render at /securityhub/detective. It owns the
// route the way InspectorEntry owns Inspector's — DetectiveDashboard is only ever
// mounted once a non-null config has been read, which is what lets the dashboard
// treat a rejected GET /summary as a plain failure rather than as "not
// configured".
//
//   availability.available === false → the DD4 panel: the reason, verbatim,
//                                      and NO Enable button
//   error                            → PortalErrorState — NEVER the setup wizard
//   config === null / disabled / failed → <DetectiveSetup/>
//   config.status === 'in_progress'  → the transitioning view (polls until done)
//   otherwise                        → <DetectiveDashboard/>
//
// THE ERROR RULE IS THE ONE THAT HAS GONE WRONG REPEATEDLY IN THIS CODEBASE. An
// early return, or a `finally` that clears the error, makes a real failure render
// as "not configured" — so the operator is shown a friendly Enable button for a
// capability that is already enabled and merely unreachable, and pressing it tears
// down a working deployment. `finally` here sets LOADING ONLY.
//
// partial_failure deliberately routes to the dashboard, not back to the wizard:
// the graph exists and most members are in it, and the per-member Retry on the
// Enrollment page is the repair path. Re-running the wizard would delete the graph
// and start over.
export default function DetectiveEntry() {
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const pollRef = useRef(null);
  const pollFailuresRef = useRef(0);

  const load = useCallback(async ({ showSpinner = true } = {}) => {
    if (showSpinner) setLoading(true);
    try {
      // Availability is read only on user-visible loads. It answers from the
      // deployment mode and the SaaS pairing, neither of which can change during
      // an in-flight enable, so re-reading it on every 3s poll would double the
      // request rate to say the same thing.
      if (showSpinner) {
        // Its OWN try/catch, deliberately. A failed availability call must not
        // decide anything: it is not "unavailable", and it must not abort the
        // config load either — a tenant whose Detective is enabled and healthy
        // has to keep working when this one auxiliary route is unreachable. The
        // failure is swallowed here and `availability` stays null, which the
        // wizard reads as "no allowlist" and degrades to the unfiltered region
        // list. If the config load fails too, THAT is what reports the error.
        let av = null;
        try {
          av = await getDetectiveAvailability();
          setAvailability(av?.data || null);
        } catch (_) {
          setAvailability(null);
        }
        // Short-circuit ONLY on a definite false. An availability call that
        // FAILED leaves `available` undefined, and reading that as "unavailable"
        // would state a reason to the operator that is not true.
        // `!av?.data?.available` would be exactly that bug.
        if (av?.data?.available === false) {
          setConfig(null);
          // Cleared because this branch is a SUCCESSFUL answer, not a blanket
          // reset: the availability call resolved and told us the capability is
          // not offered here. Leaving a stale error from an earlier attempt would
          // hide the reason behind a red card. This is not the forbidden
          // `setError('')` — that one lives in `finally` and fires on the failure
          // path too.
          setError('');
          return;
        }
      }
      const res = await getDetectiveConfig();
      // A tenant that has never enabled Detective resolves with data: null. That
      // is the wizard signal, not a fault — only a rejection is an error.
      setConfig(res?.data || null);
      pollFailuresRef.current = 0;
      setError('');
    } catch (e) {
      // Set the error and DO NOT clear config: a stale view with a visible error
      // is honest, a setup wizard is a lie.
      //
      // The error card is checked before the in_progress branch, so reporting
      // every rejection would let one blipped poll replace a multi-minute enable
      // with a red failure — during exactly the operation this screen exists to
      // narrate. A user-visible load has nothing to fall back on and reports at
      // once; a background poll has to fail repeatedly first, and the next
      // successful tick clears it.
      const message = e.message || 'Could not load Amazon Detective';
      if (showSpinner) {
        setError(message);
      } else {
        pollFailuresRef.current += 1;
        if (pollFailuresRef.current >= MAX_POLL_FAILURES) setError(message);
      }
    } finally {
      // ONLY loading. Never setError('') here — see the header note.
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll during any in_progress transition so the view advances by itself once
  // the orchestrator goroutine finishes. Depending on the status alone (not the
  // whole config) keeps the interval from being torn down and rebuilt on every
  // tick, which would reset the cadence each time the progress percentage moves.
  useEffect(() => {
    if (config?.status !== 'in_progress') return undefined;
    // Each transition starts with a clean slate — failures tolerated during an
    // earlier operation must not count against this one.
    pollFailuresRef.current = 0;
    pollRef.current = setInterval(() => load({ showSpinner: false }), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [config?.status, load]);

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{detectiveHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading Amazon Detective…" color={DETECTIVE_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // Checked BEFORE every other terminal branch. Whatever else is true, a failure
  // must never render the setup wizard.
  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{detectiveHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => load()} />
        </motion.div>
      </motion.div>
    );
  }

  // DD4: this installation cannot offer Detective. The reason is the backend's
  // words, VERBATIM — it is spelled once, in detectiveAvailability, and every
  // surface reads it from there rather than carrying its own paraphrase. The
  // fallback covers only a malformed response: a panel with neither a reason nor
  // a CTA is the one outcome worse than either.
  //
  // There is deliberately NO Enable button. `available === false` is a statement
  // about the deployment, not about this tenant's choices, and an Enable button
  // here would lead straight to the enable route's 412.
  if (availability?.available === false) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{detectiveHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalEmptyState
              icon={<TravelExploreOutlinedIcon sx={{ fontSize: 28 }} />}
              title="Amazon Detective is not available on this installation"
              description={availability.reason || 'This capability is not available on this installation.'}
              color={DETECTIVE_COLOR}
            />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (config?.status === 'in_progress') {
    const { title, body } = transitionCopy(config.progressLabel);
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{detectiveHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalSectionHeader
              icon={<TravelExploreOutlinedIcon sx={{ fontSize: 20 }} />}
              title={title}
              color={DETECTIVE_COLOR}
              mb={1.5}
            />
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
              {body}
            </Typography>
            <OperationProgress percent={config.progress} label={config.progressLabel || 'Working…'} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // Terminal "nothing is enrolled" states — surface the setup wizard so the user
  // can re-run. EnableDetective drops the settled prior config and its children
  // server-side before creating a new one, so submitting again is safe.
  //
  // `availability` is handed down so the wizard's region picker can intersect
  // AWS_REGIONS with supportedRegions WITHOUT making a second request for a
  // payload this component already holds.
  if (!config || config.status === 'disabled' || config.status === 'failed') {
    return (
      <DetectiveSetup
        availability={availability}
        onComplete={load}
        priorFailure={config?.status === 'failed' ? config.errorMessage : null}
      />
    );
  }

  return <DetectiveDashboard config={config} onConfigChange={load} />;
}
